// src/components/customer/CustomerCatalogWithCart.js
// Shows customerVisible items - grayed out if not sampleable

import React, { useMemo, useState } from "react";
import {
  Search,
  Package,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Maximize2,
  Minimize2,
  Image as ImageIcon,
} from "lucide-react";

const pickImageUrl = (inv) => {
  return (
    inv?.imageUrl ||
    inv?.imageURL ||
    inv?.photoUrl ||
    inv?.photoURL ||
    inv?.image ||
    inv?.thumbnailUrl ||
    inv?.thumbnailURL ||
    inv?.thumbUrl ||
    inv?.thumbURL ||
    ""
  );
};

const CustomerCatalogWithCart = ({
  inventory = [],
  cart,
  onGoToRequest,
  onClearOrder,
}) => {
  const [q, setQ] = useState("");
  const [expandedGrid, setExpandedGrid] = useState(false);

  const filtered = useMemo(() => {
    const term = (q || "").toLowerCase().trim();
    if (!term) return inventory || [];

    return (inventory || []).filter((inv) => {
      const name = (inv?.name || "").toLowerCase();
      const sku = (inv?.sku || "").toLowerCase();
      const desc = (inv?.description || "").toLowerCase();
      const brand = (inv?.brand || "").toLowerCase();
      const category = (inv?.category || "").toLowerCase();
      return (
        name.includes(term) ||
        sku.includes(term) ||
        desc.includes(term) ||
        brand.includes(term) ||
        category.includes(term)
      );
    });
  }, [inventory, q]);

  const distinctCount = cart?.distinctCount || 0;
  const totalUnits = cart?.totalUnits || 0;
  const hasCart = distinctCount > 0;

  const gridClass = expandedGrid
    ? "grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3"
    : "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Catalog (Menu)</h3>
          <p className="text-xs text-gray-500">
            Add items here — they'll appear in your Sample Order form.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="w-full sm:w-[320px] relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setExpandedGrid((v) => !v)}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50"
            title={expandedGrid ? "Minimize grid (3 per row)" : "Expand grid (5 per row)"}
          >
            {expandedGrid ? (
              <>
                <Minimize2 className="w-4 h-4" />
                <span className="hidden sm:inline">Min</span>
              </>
            ) : (
              <>
                <Maximize2 className="w-4 h-4" />
                <span className="hidden sm:inline">Max</span>
              </>
            )}
          </button>

          <button
            type="button"
            disabled={!hasCart}
            onClick={onGoToRequest}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={hasCart ? "Open your sample order form" : "Add items first"}
          >
            <ShoppingCart className="w-4 h-4" />
            {hasCart ? `Go to Order (${distinctCount})` : "Go to Order"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-sm text-gray-700 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-blue-600" />
          {hasCart ? (
            <span>
              <span className="font-semibold">{distinctCount}</span>{" "}
              item{distinctCount === 1 ? "" : "s"} •{" "}
              <span className="font-semibold">{totalUnits}</span>{" "}
              unit{totalUnits === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="text-gray-500">Your order is empty</span>
          )}
        </div>

        <button
          type="button"
          disabled={!hasCart}
          onClick={onClearOrder || (() => cart?.clear?.())}
          className="inline-flex items-center justify-center gap-2 px-2.5 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Trash2 className="w-4 h-4" />
          Clear
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-gray-200 bg-white rounded-xl">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-gray-800">No items found</div>
          <div className="text-xs text-gray-500 mt-1">
            Try a different search, or your distributor may not have customer-visible inventory yet.
          </div>
        </div>
      ) : (
        <div className={gridClass}>
          {filtered.map((inv) => {
            const tags = [
              inv?.sku ? `SKU: ${inv.sku}` : null,
              inv?.brand ? inv.brand : null,
              inv?.category ? inv.category : null,
            ].filter(Boolean);

            const qty = cart?.getQty ? cart.getQty(inv.id) : 0;
            const imgUrl = pickImageUrl(inv);
            
            // ✅ KEY LOGIC: Not sampleable = grayed out but visible
            const notSampleable = inv?.sampleable !== true;

            return (
              <div
                key={inv.id}
                className={`bg-white border border-gray-200 rounded-xl shadow-sm p-3 hover:shadow-md transition-shadow ${
                  notSampleable ? "opacity-60" : ""
                }`}
              >
                <div className="relative w-full h-28 rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={inv?.name || "Item"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}

                  {!notSampleable && (
                    <div className="absolute top-2 right-2 rounded-md bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 text-[11px] font-semibold">
                      Sampleable
                    </div>
                  )}
                  
                  {notSampleable && (
                    <div className="absolute top-2 right-2 rounded-md bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold">
                      Not Available
                    </div>
                  )}
                </div>

                <div className="mt-3 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {inv?.name || "Unnamed item"}
                  </div>
                  {inv?.description ? (
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {inv.description}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mt-1">No description</div>
                  )}
                </div>

                {tags.length ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {tags.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="mt-3 flex items-center justify-between gap-2">
                  {notSampleable ? (
                    <div className="text-[11px] text-amber-700 font-medium">
                      Currently unavailable
                    </div>
                  ) : qty > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => cart?.setQty?.(inv.id, qty - 1)}
                          className="h-8 w-8 flex items-center justify-center hover:bg-gray-50"
                          title="Decrease"
                        >
                          <Minus className="w-4 h-4 text-gray-700" />
                        </button>

                        <div className="min-w-[38px] h-8 flex items-center justify-center text-sm font-semibold text-gray-900 border-x border-gray-200">
                          {qty}
                        </div>

                        <button
                          type="button"
                          onClick={() => cart?.add?.(inv.id, 1)}
                          className="h-8 w-8 flex items-center justify-center hover:bg-gray-50"
                          title="Increase"
                        >
                          <Plus className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => cart?.add?.(inv.id, 1)}
                      className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Add
                    </button>
                  )}

                  {!notSampleable && qty > 0 ? (
                    <button
                      type="button"
                      onClick={() => cart?.remove?.(inv.id)}
                      className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-xs font-semibold"
                      title="Remove from order"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Remove</span>
                    </button>
                  ) : !notSampleable ? (
                    <div className="text-[11px] text-gray-400">Add to order</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerCatalogWithCart;